import {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { Plural } from '@lingui/react/macro'

import type { Photos } from '@jotta/types/Photos'
import { AppLayoutContext } from '@jotta/ui/AppLayout'
import type { BoxProps } from '@jotta/ui/Box'
import { Box } from '@jotta/ui/Box'
import { Button } from '@jotta/ui/Button'
import { Flex } from '@jotta/ui/Flex'
import { Headline2 } from '@jotta/ui/Headline'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { useBrandStore } from '@jotta/ui/useBrandTheme'

import { useSelectionStore } from '../../store/PhotoContext'
import type { SelectablePhoto } from '../../store/SelectionStore'
import { PhotoCheckbox } from '../PhotoCheckbox/PhotoCheckbox'

import { PhotoRow } from './PhotoRow'
import type { GalleryRef } from './useGroupByDayAndRow'
import { isTimelineHeader, useGroupByDayAndRow } from './useGroupByDayAndRow'
import { useVirtualTimeline } from './useVirtualTimeline'

const HeaderItem = observer<{ day: number; photos: SelectablePhoto[] }>(
  ({ day, photos }) => {
    const selectionStore = useSelectionStore()

    const isSelected = photos.every(photo => selectionStore.has(photo.id))
    const onToggleSelect = (selected: boolean) => {
      if (selected) {
        for (const photo of photos) {
          selectionStore.add(photo)
        }
      } else {
        for (const photo of photos) {
          selectionStore.delete(photo.id)
        }
      }
    }

    const clickHandler = () => onToggleSelect(!isSelected)

    return (
      <Box sx={{ overflow: 'hidden', ml: [2, 0] }}>
        <Flex
          sx={{
            alignItems: 'baseline',
            pt: 4,
            pb: '14px',
            pr: 2,
            justifyContent: 'start',
            transition: 'margin 0.2s',
            gap: 2,
            ml: !isSelected ? -4 : 0,
            '@media (hover: hover) and (pointer: fine)': {
              ':hover': {
                ml: 0,
              },
            },
          }}
        >
          <PhotoCheckbox
            className="self-center"
            isSelected={isSelected}
            onToggleSelect={onToggleSelect}
          />
          <Button
            onClick={clickHandler}
            sx={{ cursor: 'pointer', display: 'flex' }}
            className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-m3-primary"
            tabIndex={0}
          >
            <Headline2 variant="styles.timelineHeading">
              {dayjs.utc(day).format('LL')}
            </Headline2>
          </Button>
          <span
            sx={{
              fontSize: 2,
              opacity: 0.8,
              fontWeight: 'body',
              color: 'graySlateBgColor',
              cursor: 'default',
            }}
          >
            {/* Lingui creates a duplicate empty translation if we use imageCount directly */}
            <Plural
              value={Number(photos.length)}
              one="# photo"
              other="# photos"
            />
          </span>
        </Flex>
      </Box>
    )
  },
)

const PHOTO_HEIGHT = 240
const TARGET_MOBILE_SIZE = 90
const MAX_MOBILE_ROW_LEN = 4

export const Timeline = observer<
  Partial<BoxProps> & {
    photos: readonly Photos.Media[]
    showSimilarSearch?: boolean
    showWaitinglistBanners?: boolean
  },
  GalleryRef
>(
  forwardRef(function Timeline(
    { photos = [], showSimilarSearch = false, showWaitinglistBanners = false },
    ref,
  ) {
    const [rowAspect, setRowAspect] = useState<[number, number]>()
    const branding = useBrandStore()
    const rows = useGroupByDayAndRow(
      rowAspect || [8, 1],
      photos,
      !branding.isMobileTimeline,
    )
    const [listElement, setListElement] = useState<HTMLDivElement>()

    // Scroll to index
    const photoIndex = useMemo(
      () =>
        rows.reduce((map, row, i) => {
          if (!isTimelineHeader(row)) {
            for (const el of row.elements) {
              map.set(el.id, i)
            }
          }
          return map
        }, new Map<string, number>()),
      [rows],
    )

    useImperativeHandle(
      ref,
      () => ({
        scrollToMediaById(id: string) {
          const index = photoIndex.get(id)
          // if (index) {
          //   virtualRef.current.scrollToIndex(index)
          // }
        },
      }),
      [photoIndex],
    )

    const bk = branding.theme.branding.photos.timeline.mobileTimelineBreakpoint

    const onSizeChange = useCallback(
      (width: number) => {
        if (width == 0) {
          return
        }
        if (width >= bk) {
          const newRowAspect: [number, number] = [
            Math.max(2, Math.ceil(width / PHOTO_HEIGHT)),
            1,
          ]
          setRowAspect(prev => {
            if (
              prev &&
              prev[0] === newRowAspect[0] &&
              prev[1] === newRowAspect[1]
            ) {
              return prev
            }
            return newRowAspect
          })
        } else {
          const newRowAspect: [number, number] = [
            Math.max(
              1,
              Math.min(
                MAX_MOBILE_ROW_LEN,
                Math.trunc(width / TARGET_MOBILE_SIZE),
              ),
            ),
            1,
          ]
          setRowAspect(prev => {
            if (
              prev &&
              prev[0] === newRowAspect[0] &&
              prev[1] === newRowAspect[1]
            ) {
              return prev
            }
            return newRowAspect
          })
        }
      },
      [setRowAspect, bk],
    )

    const getSize = useCallback(
      (i: number, width: number) => {
        if (!rowAspect) {
          return 0
        }
        const row = rows[i]
        if (row.content === 'header') {
          return 60.5
        }

        return (width * rowAspect[1]) / rowAspect[0]
      },
      [rows, rowAspect],
    )

    const { scrollElement } = useContext(AppLayoutContext)

    const virtualizer = useVirtualTimeline({
      rows,
      parentElement: listElement,
      getSize,
      onSizeChange,
      scrollingParent: scrollElement.current,
    })

    return (
      <Box
        sx={{
          ml: 3,
          mr: [3, 64],
          userSelect: 'none',
          height: '100%',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        id="timeline"
      >
        <LoadingOverlay id="Timeline" position="absolute" open={!rowAspect} />

        <Box
          sx={{
            position: 'relative',
            pb: ['var(--s3)', 'calc(var(--content-bottom) + var(--s3))'],
            boxSizing: 'content-box',
          }}
          ref={setListElement}
        >
          {virtualizer.virtualItems.map(
            ({ virtualIndex, virtualStyle, row }) => {
              return (
                <div key={virtualIndex} style={virtualStyle}>
                  {isTimelineHeader(row) ? (
                    <HeaderItem day={row.day} photos={row.photos} />
                  ) : (
                    <PhotoRow row={row} showSimilarSearch={showSimilarSearch} />
                  )}
                </div>
              )
            },
          )}
        </Box>
      </Box>
    )
  }),
)
